import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton, useAsync, useToast } from '../../components/ui';
import { Approval, APPROVAL_STATUS, JourneyDetail, pct, sampleVariables, shortHash } from '../../journeys/model';
import { api, ApiError, Template, TemplatePreview, TemplateVersion } from '../../lib/api';
import { dateTime, num } from '../../lib/format';
import { t } from '../../lib/i18n';
import { errorText } from '../Contacts';

type VersionContent = TemplateVersion & { html: string | null; text: string | null };

const who = (actor: string | null) => (!actor ? '—' : actor.startsWith('user:') ? t('участник команды') : actor.startsWith('api_key:') ? 'API-ключ' : actor);

/** The email of a step exactly as approved: the pinned template version, variables shown by name. */
function PinnedEmail({ alias, version, step }: { alias: string; version: number; step: string }) {
  const r = useAsync(async () => {
    const list = await api.get<Template[]>('/templates');
    const tpl = list.find((x) => x.alias === alias);
    if (!tpl) return null;
    const versions = await api.get<VersionContent[]>(`/templates/${tpl.id}/versions`);
    const v = versions.find((x) => x.version === version);
    if (!v) return null;
    const names = new Set<string>();
    for (const src of [v.subject, v.html ?? '', v.text ?? '']) for (const m of src.matchAll(/\{\{\{?\s*([A-Za-z_][\w.]*)\s*\}?\}\}/g)) names.add(m[1]);
    return api.post<TemplatePreview>('/templates/preview', { subject: v.subject, html: v.html, text: v.text, variables: sampleVariables([...names]) });
  }, [alias, version]);
  if (r.loading) return <Skeleton h={120} />;
  if (!r.data) return <div className="small muted">{t('Версия шаблона не найдена.')}</div>;
  return r.data.html ? <iframe title={t('Письмо шага {step}', { step })} sandbox="" srcDoc={r.data.html} className="mail-preview" /> : <pre className="code" style={{ whiteSpace: 'pre-wrap' }}>{r.data.text}</pre>;
}

export function ApprovalSnapshot({ a }: { a: Approval }) {
  const s = a.summary;
  const aud = s.audience_estimate;
  return (
    <div className="stack">
      <div className="approval-hero">
        <div><div className="label">{t('Триггер')}</div><div className="v">{s.trigger}</div></div>
        <div><div className="label">{t('Поток и отправитель')}</div><div className="v">{s.stream === 'marketing' ? t('Маркетинг') : t('Транзакционные')} · {s.from ?? '—'}</div></div>
        <div>
          <div className="label">{t('Аудитория (оценка)')}</div>
          <div className="v">
            {aud.triggers_last_7d != null ? t('{n} срабатываний за 7 дней', { n: num(aud.triggers_last_7d) }) : aud.segment_count != null ? t('{n} в сегменте', { n: num(aud.segment_count) }) : t('зависит от вызовов API или дат')}
          </div>
          {aud.eligible_share != null && <div className="small muted">{t('подходит {p}', { p: pct(aud.eligible_share, 0) })}{aud.excluded ? ` · ${t('без согласия {n}', { n: num(aud.excluded.no_consent) })} · ${t('подавлены {n}', { n: num(aud.excluded.suppressed) })}` : ''}</div>}
        </div>
        <div><div className="label">{t('Контрольная группа')}</div><div className="v">{s.holdout_percent ? t('{p} % — писем не получат', { p: s.holdout_percent }) : t('нет — прирост не измерить')}</div></div>
        <div><div className="label">{t('Частота')}</div><div className="v">{s.frequency.cap}{s.frequency.smart_sending_hours ? ` · ${t('умная отправка {h} ч', { h: s.frequency.smart_sending_hours })}` : ''}</div></div>
        <div><div className="label">{t('Время')}</div><div className="v">{s.quiet_hours ? t('Тихие часы {h}', { h: s.quiet_hours }) : t('Без тихих часов')}{s.max_lateness_hours ? ` · ${t('опоздание до {h} ч', { h: s.max_lateness_hours })}` : ''}</div></div>
        <div><div className="label">{t('Цель')}</div><div className="v">{s.goal ?? t('не задана')}</div></div>
        <div><div className="label">{t('Версия')}</div><div className="v">v{s.version} · <code>{shortHash(s.hash)}</code></div></div>
      </div>
      {s.transactional_notice && <div className="callout warn"><span><b>DAT-05.</b> {s.transactional_notice}</span></div>}
      {s.warnings.length > 0 && <div className="callout warn"><div><b>{t('Предупреждения:')}</b> {s.warnings.join('; ')}</div></div>}
      {s.diff_from_active && (
        <div className="callout">
          <div>
            <b>{t('Отличия от работающей версии:')}</b>{' '}
            {[...s.diff_from_active.added.map((x) => `+ ${x}`), ...s.diff_from_active.removed.map((x) => `− ${x}`)].join(', ') || t('шаги те же')}
            {s.diff_from_active.changed.length > 0 && <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{s.diff_from_active.changed.slice(0, 12).map((c) => <li key={c}><code>{c}</code></li>)}</ul>}
          </div>
        </div>
      )}
      <div className="stack">
        <h3 style={{ margin: 0 }}>{t('Письма ({n})', { n: s.emails.length })}</h3>
        {s.emails.map((e) => (
          <details key={e.step} className="card card-pad" open={s.emails.length === 1}>
            <summary style={{ cursor: 'pointer' }}>
              <b>{e.subject ?? '—'}</b> <span className="small muted">· {t('шаг')} <code>{e.step}</code> · {e.template} v{e.template_version} · {e.stream === 'transactional' ? t('сервисное') : t('маркетинг')} · {e.from}</span>
            </summary>
            <div style={{ marginTop: 10 }}><PinnedEmail alias={e.template} version={e.template_version} step={e.step} /></div>
          </details>
        ))}
      </div>
      {s.notifications && s.notifications.length > 0 && (
        <div className="stack">
          <h3 style={{ margin: 0 }}>{t('Уведомления по каналам ({n})', { n: s.notifications.length })}</h3>
          {s.notifications.map((n) => (
            <div key={n.step} className="card card-pad small">
              {t('шаг')} <code>{n.step}</code> · {t('политика')} <Link to={`/channels/policies/${n.policy}`}><code>{n.policy}</code></Link> v{n.policy_version} · {t('шаблон')} <Link to={`/channels/templates/${n.template}`}><code>{n.template}</code></Link> v{n.template_version} · {n.stream === 'transactional' ? t('сервисное') : t('маркетинг')}
            </div>
          ))}
          <div className="small muted">{t('Версии закреплены в хеше сценария: правка политики или шаблона потребует нового согласования.')}</div>
        </div>
      )}
      {s.honest_metrics && <div className="small muted">{s.honest_metrics}</div>}
    </div>
  );
}

/**
 * Approval (docs/journeys/SPEC.md §4.1, §3.11): the snapshot the approver sees, big; the pinned emails; «Согласовать» /
 * «Вернуть с комментарием»; after the approval — «Запустить». The approval is bound to the version hash: any change of
 * the graph is a new version that needs a new approval.
 */
export function JourneyApproval({ journey, owner, writer, onChanged }: { journey: JourneyDetail; owner: boolean; writer: boolean; onChanged: () => void }) {
  const toast = useToast();
  const list = useAsync(() => api.get<{ items: Approval[] }>(`/journeys/${journey.id}/approvals`), [journey.id, journey.latest_version, journey.active_version, journey.status]);
  const [comment, setComment] = useState('');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const latest = journey.latest;
  const items = list.data?.items ?? [];
  const current = items.find((a) => a.version === journey.latest_version && a.status !== 'void') ?? null;
  const shown = current ?? items.find((a) => a.status !== 'void') ?? null;

  async function act(fn: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setError('');
    try {
      await fn();
      toast(ok);
      setComment('');
      list.reload();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'APPROVAL_SELF' ? t('Вы автор этой версии: согласовать её должен другой владелец или администратор пространства.') : errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (list.loading && !list.data) return <Skeleton h={240} />;
  if (!latest) return <div className="callout">{t('Версий ещё нет.')}</div>;

  return (
    <div className="stack">
      {!current && (
        <div className="card card-pad stack">
          <h3 style={{ margin: 0 }}>{t('Версия {v} ещё не согласована', { v: latest.version })}</h3>
          <p className="small" style={{ margin: 0 }}>{t('Согласующий увидит снимок: триггер, аудиторию, письма ровно закреплённых версий, частоту и время. Одобрение привязано к хешу')} <code>{shortHash(latest.hash)}</code> {t('— любое изменение графа потребует нового согласования.')}</p>
          {writer ? (
            <button className="btn btn-primary" style={{ alignSelf: 'flex-start' }} disabled={busy} onClick={() => act(() => api.post(`/journeys/${journey.id}/versions/${latest.version}/approval-requests`, { hash: latest.hash }), t('Запрос на согласование отправлен'))}>
              {t('Запросить согласование v{v}', { v: latest.version })}
            </button>
          ) : (
            <p className="small muted">{t('Запросить согласование может участник с ролью разработчика и выше.')}</p>
          )}
        </div>
      )}

      {shown && (
        <div className="card">
          <div className="card-head">
            <h3>{t('Согласование v{v}', { v: shown.version })}</h3>
            <div className="row wrap" style={{ gap: 6 }}>
              <span className={`pill ${APPROVAL_STATUS[shown.status]?.tone ?? ''}`}>{t(APPROVAL_STATUS[shown.status]?.label ?? shown.status)}</span>
              {shown.self_approved && <span className="self-approved" title={t('В пространстве нет второго владельца или администратора')}>{t('Согласовано самостоятельно')}</span>}
            </div>
          </div>
          <div className="card-pad stack">
            {shown.self_approved && <div className="callout warn">{t('Согласовано самостоятельно: в пространстве нет второго владельца или администратора. Это видно в истории и в MailOcean.')}</div>}
            <ApprovalSnapshot a={shown} />
            {shown.comment && <div className="callout"><span><b>{t('Комментарий:')}</b> {shown.comment}</span></div>}
            {error && <div className="callout bad">{error}</div>}

            {shown.status === 'pending' && owner && (
              <div className="stack" style={{ paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                {shown.summary.transactional_notice && (
                  <label className="row small" style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> {t('Подтверждаю: сервисные письма сценария не содержат рекламы (DAT-05)')}
                  </label>
                )}
                <textarea className="input" rows={2} placeholder={t('Комментарий (обязателен, если возвращаете)')} value={comment} onChange={(e) => setComment(e.target.value)} aria-label={t('Комментарий к решению')} />
                <div className="row wrap">
                  <button className="btn btn-primary" disabled={busy || (!!shown.summary.transactional_notice && !ack)} onClick={() => act(() => api.post(`/journeys/${journey.id}/approvals/${shown.id}/decision`, { decision: 'approve', hash: shown.hash, ...(comment.trim() ? { comment: comment.trim() } : {}) }), t('Согласовано — можно запускать'))}>{t('Согласовать')}</button>
                  <button className="btn" disabled={busy || comment.trim().length < 3} onClick={() => act(() => api.post(`/journeys/${journey.id}/approvals/${shown.id}/decision`, { decision: 'reject', hash: shown.hash, comment: comment.trim() }), t('Возвращено автору с комментарием'))}>{t('Вернуть с комментарием')}</button>
                </div>
              </div>
            )}
            {shown.status === 'pending' && !owner && <div className="callout">{t('Ждёт решения владельца или администратора пространства.')}</div>}

            {shown.status === 'approved' && shown.version !== journey.active_version && (
              owner ? (
                <div className="row wrap" style={{ paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                  <button className="btn btn-primary" disabled={busy || journey.status === 'stopped'} onClick={() => act(() => api.post(`/journeys/${journey.id}/versions/${shown.version}/publish`, { hash: shown.hash }), journey.locked_by_staff ? t('Версия опубликована; сценарий остаётся на паузе MailOcean') : t('Запущено: сценарий течёт'))}>
                    {t('Запустить v{v}', { v: shown.version })}
                  </button>
                  <span className="small muted">{journey.active_version ? t('{state} v{av}. Участия в пути доходят по своей версии; новые — по v{v}.', { state: journey.status === 'active' ? t('Сейчас работает') : t('Активна'), av: journey.active_version, v: shown.version }) : t('Новые участники начнут входить сразу после запуска.')}</span>
                </div>
              ) : (
                <div className="callout">{t('Согласовано. Запустить может владелец или администратор.')}</div>
              )
            )}
            {shown.status === 'approved' && shown.version === journey.active_version && owner && (
              <div className="row wrap" style={{ paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <span className="small muted grow">{journey.status === 'active' ? t('Эта версия работает.') : journey.status === 'stopped' ? t('Сценарий остановлен на этой версии.') : t('Эта версия активна, сценарий на паузе.')}</span>
                <button className="btn btn-sm btn-danger" disabled={busy} onClick={() => confirm(t('Отозвать согласование? Сценарий встанет на паузу.')) && act(() => api.post(`/journeys/${journey.id}/approvals/${shown.id}/revoke`, {}), t('Согласование отозвано, сценарий на паузе'))}>{t('Отозвать согласование')}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div className="card">
          <div className="card-head"><h3>{t('История согласований')}</h3></div>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>{t('Версия')}</th><th>{t('Статус')}</th><th>{t('Запросил')}</th><th>{t('Решение')}</th><th>{t('Комментарий')}</th></tr></thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id}>
                    <td>v{a.version} <code className="small">{shortHash(a.hash)}</code></td>
                    <td><span className={`pill ${APPROVAL_STATUS[a.status]?.tone ?? ''}`}>{t(APPROVAL_STATUS[a.status]?.label ?? a.status)}</span>{a.self_approved && <div className="small" style={{ color: '#b67e06' }}>{t('самостоятельно')}</div>}</td>
                    <td className="small">{who(a.requested_by)}<div className="muted">{dateTime(a.requested_at)}</div></td>
                    <td className="small">{a.decided_at ? <>{who(a.decided_by)}<div className="muted">{dateTime(a.decided_at)}</div></> : '—'}</td>
                    <td className="small" style={{ overflowWrap: 'anywhere' }}>{a.comment ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
